import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { reportsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";

// Format a Date as YYYY-MM-DD in Israel timezone (not UTC)
const toILDateStr = (d) =>
  new Intl.DateTimeFormat("sv", { timeZone: "Asia/Jerusalem" }).format(d);

const today = () => toILDateStr(new Date());
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toILDateStr(d);
};

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b">
        <h2 className="font-semibold text-gray-700">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function TableSkeleton({ cols = 4 }) {
  return (
    <div className="space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex gap-4">
          {[...Array(cols)].map((__, j) => (
            <div key={j} className="h-4 bg-gray-100 rounded flex-1 animate-pulse" />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── 1. By Company ────────────────────────────────────────────────────────────

function ByCompany() {
  const { data = [], isLoading, isError: failed } = useQuery({
    queryKey: ["report-by-company"],
    queryFn: reportsApi.byCompany,
  });

  return (
    <SectionCard title="קריאות לפי חברה">
      {isLoading ? (
        <TableSkeleton cols={4} />
      ) : failed && data.length === 0 ? (
        <p role="alert" className="text-red-600 text-sm">לא ניתן לטעון את הדוח</p>
      ) : data.length === 0 ? (
        <p className="text-gray-400 text-sm italic">אין נתונים</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-right px-4 py-2.5">שם חברה</th>
                <th className="text-right px-4 py-2.5">סה"כ קריאות</th>
                <th className="text-right px-4 py-2.5">פתוחות</th>
                <th className="text-right px-4 py-2.5">סגורות</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{row.company_name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{row.total}</td>
                  <td className="px-4 py-2.5">
                    {row.open_count > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        {row.open_count}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                      {row.closed_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ─── 2. By Technician ─────────────────────────────────────────────────────────

function ByTechnician() {
  const { data = [], isLoading, isError: failed } = useQuery({
    queryKey: ["report-by-technician"],
    queryFn: reportsApi.byTechnician,
  });

  return (
    <SectionCard title="קריאות לפי טכנאי">
      {isLoading ? (
        <TableSkeleton cols={5} />
      ) : failed && data.length === 0 ? (
        <p role="alert" className="text-red-600 text-sm">לא ניתן לטעון את הדוח</p>
      ) : data.length === 0 ? (
        <p className="text-gray-400 text-sm italic">אין נתונים</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-right px-4 py-2.5">שם טכנאי</th>
                <th className="text-right px-4 py-2.5">סה"כ קריאות</th>
                <th className="text-right px-4 py-2.5">פתוחות</th>
                <th className="text-right px-4 py-2.5">סגורות</th>
                <th className="text-right px-4 py-2.5">ממוצע זמן טיפול</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{row.tech_name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{row.total}</td>
                  <td className="px-4 py-2.5">
                    {row.open_count > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        {row.open_count}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                      {row.closed_count}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 text-xs">
                    {row.avg_hours != null
                      ? row.avg_hours < 1
                        ? `${Math.round(row.avg_hours * 60)} דק׳`
                        : `${row.avg_hours} שע׳`
                      : <span className="text-gray-400 italic">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ─── 3. By Time ───────────────────────────────────────────────────────────────

function ByTime() {
  const [start, setStart] = useState(daysAgo(29));
  const [end,   setEnd]   = useState(today());

  const { data = [], isLoading, isError: failed } = useQuery({
    queryKey: ["report-by-time", start, end],
    queryFn:  () => reportsApi.byTime(start, end),
    enabled:  !!(start && end),
  });

  return (
    <SectionCard title="קריאות לפי זמן">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">מתאריך</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">עד תאריך</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-xs"
          />
        </div>
        <div className="flex gap-2">
          {[
            { label: "7 ימים", days: 6 },
            { label: "30 ימים", days: 29 },
            { label: "90 ימים", days: 89 },
          ].map(({ label, days }) => (
            <button
              key={days}
              onClick={() => { setStart(daysAgo(days)); setEnd(today()); }}
              className="text-xs border rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition-colors text-gray-600"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton cols={3} />
      ) : failed && data.length === 0 ? (
        <p role="alert" className="text-red-600 text-sm">לא ניתן לטעון את הדוח</p>
      ) : data.length === 0 ? (
        <p className="text-gray-400 text-sm italic">אין נתונים בטווח זה</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-right px-4 py-2.5">תאריך</th>
                <th className="text-right px-4 py-2.5">נפתחו</th>
                <th className="text-right px-4 py-2.5">נסגרו</th>
              </tr>
            </thead>
            <tbody>
              {[...data].reverse().map((row) => (
                <tr key={row.date} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                    {new Date(row.date + "T12:00:00").toLocaleDateString("he-IL")}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.opened > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        {row.opened}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.closed > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        {row.closed}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ─── 4. Summary ───────────────────────────────────────────────────────────────

function Summary() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["report-summary"],
    queryFn:  reportsApi.summary,
  });

  const periods = [
    { key: "today", label: "היום" },
    { key: "week",  label: "השבוע" },
    { key: "month", label: "החודש" },
  ];

  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleTimeString("he-IL", {
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        timeZone: "Asia/Jerusalem",
      })
    : null;

  const goTo = (status, period) =>
    navigate(`/tickets?status=${status}&period=${period}`);

  return (
    <SectionCard title="סיכום תקופתי">
      {isLoading ? (
        <TableSkeleton cols={3} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {periods.map(({ key, label }) => (
              <div key={key} className="rounded-xl border p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                <div className="flex justify-around">
                  <button
                    onClick={() => goTo("all_open", key)}
                    className="text-center px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors group"
                    title={`הצג קריאות שנפתחו — ${label}`}
                  >
                    <p className="text-3xl font-extrabold text-indigo-600 group-hover:text-indigo-700">
                      {data?.[key]?.opened ?? "—"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 group-hover:text-indigo-600">נפתחו ↗</p>
                  </button>
                  <div className="w-px bg-gray-100" />
                  <button
                    onClick={() => goTo("closed", key)}
                    className="text-center px-4 py-2 rounded-lg hover:bg-green-50 transition-colors group"
                    title={`הצג קריאות שנסגרו — ${label}`}
                  >
                    <p className="text-3xl font-extrabold text-green-600 group-hover:text-green-700">
                      {data?.[key]?.closed ?? "—"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 group-hover:text-green-600">נסגרו ↗</p>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {generatedAt && (
            <p className="text-xs text-gray-400 mt-4 text-left">
              עודכן לאחרונה: {generatedAt}
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}

// ─── AI Operations ────────────────────────────────────────────────────────────

function AiOperations() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["report-ai-ops", days],
    queryFn: () => reportsApi.aiOperations(days),
  });

  const stat = (label, value, color = "text-gray-800", unit = "") => (
    <div className="text-center">
      <p className={`text-2xl font-bold ${color}`}>{value ?? "—"}{unit}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );

  return (
    <SectionCard title="⚡ ריצות AI">
      <div className="flex items-center gap-2 mb-4">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              days === d
                ? "bg-brand-600 text-white"
                : "border text-gray-600 hover:bg-gray-50"
            }`}
          >
            {d} ימים
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : data ? (
        <div className="space-y-5">
          {/* Summary chips */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stat("סה״כ ריצות", data.total_runs)}
            {stat("הצליחו", data.succeeded, "text-green-600")}
            {stat("נכשלו", data.failed, "text-red-600")}
            {stat("ממתינות לאישור", data.pending_review, data.pending_review > 0 ? "text-orange-600" : "text-gray-400")}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stat("אחוז הצלחה", data.success_rate != null ? `${data.success_rate}%` : null)}
            {stat("עלות כוללת", data.total_cost_usd != null ? `$${data.total_cost_usd.toFixed(4)}` : null)}
            {stat("עלות ממוצעת", data.avg_cost_usd != null ? `$${data.avg_cost_usd.toFixed(4)}` : null)}
            {stat("משך ממוצע", data.avg_duration_ms != null ? (data.avg_duration_ms < 60000 ? `${(data.avg_duration_ms / 1000).toFixed(1)}ש` : `${Math.round(data.avg_duration_ms / 60000)}ד`) : null)}
          </div>

          {/* Per-agent breakdown */}
          {data.by_agent?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">לפי סוכן</p>
              <div className="space-y-1">
                {data.by_agent.sort((a, b) => b.total - a.total).map((ag, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="text-gray-700 w-40 truncate font-medium">{ag.name}</span>
                    <span className="text-gray-500">{ag.total} ריצות</span>
                    <span className="text-green-600">{ag.succeeded} הצליחו</span>
                    {ag.failed > 0 && <span className="text-red-500">{ag.failed} נכשלו</span>}
                    {ag.cost > 0 && <span className="text-gray-400 font-mono">${ag.cost.toFixed(4)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Executed actions breakdown */}
          {data.executed_actions && Object.keys(data.executed_actions).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">פעולות שבוצעו</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.executed_actions).map(([type, count]) => {
                  const labels = {
                    close_ticket: "סגירת קריאה",
                    escalate: "הסלמה",
                    send_customer_message: "מסר ללקוח",
                    add_comment: "הערה פנימית",
                  };
                  return (
                    <div key={type} className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                      <span className="text-violet-700 font-semibold text-sm">{count}</span>
                      <span className="text-violet-500 text-xs">{labels[type] ?? type}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.total_runs === 0 && (
            <p className="text-sm text-gray-400 text-center py-2">אין ריצות AI ב-{days} הימים האחרונים</p>
          )}

          <div className="flex items-center justify-between text-xs text-gray-400 border-t pt-3">
            <span>עודכן: {data.generated_at ? new Date(data.generated_at).toLocaleString("he-IL") : "—"}</span>
            <Link to="/agent-runs" className="text-brand-600 hover:underline">כל הריצות ←</Link>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

// ─── Alerts Panel ─────────────────────────────────────────────────────────────

const SEV_STYLES = {
  warning:  "border-amber-200 bg-amber-50 text-amber-800",
  critical: "border-red-200   bg-red-50   text-red-800",
};

function AlertsPanel() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [configOpen, setConfigOpen] = useState(false);
  const [cfg, setCfg] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["ai-alerts"],
    queryFn: reportsApi.alerts,
    refetchInterval: 120000,
  });

  const { data: cfgData } = useQuery({
    queryKey: ["ai-alerts-config"],
    queryFn: reportsApi.alertsConfig,
  });

  useEffect(() => {
    if (cfgData && !cfg) setCfg(cfgData);
  }, [cfgData, cfg]);

  const dispatchMutation = useMutation({
    mutationFn: reportsApi.dispatch,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-alerts"] }),
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשליחת התראות", "error"),
  });

  const configMutation = useMutation({
    mutationFn: reportsApi.setAlertsConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-alerts-config"] });
      qc.invalidateQueries({ queryKey: ["ai-alerts"] });        // the alerts shown were evaluated against the old thresholds
      setConfigOpen(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת הגדרות התראות", "error"),
  });

  const currentCfg = cfg ?? cfgData ?? {};
  const alerts = data?.alerts || [];
  const hasAlerts = alerts.length > 0;

  return (
    <SectionCard title="התראות AI">
      {isLoading ? (
        <TableSkeleton cols={3} />
      ) : (
        <div className="space-y-3">
          {/* Alert badges */}
          {isError && !hasAlerts ? (
            // A failed request (403 for a non-admin, 5xx, network) is not "no alerts", including a failed
            // background refetch that still holds an older healthy result. Alerts we do hold stay visible.
            <div role="alert" className="flex items-center gap-2 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <span>?</span>
              <span>לא ניתן לטעון את התראות ה-AI כרגע — מצב המדדים אינו ידוע</span>
            </div>
          ) : !hasAlerts ? (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
              <span>✓</span>
              <span>כל מדדי ה-AI תקינים ({data?.window_hours || 24}ש אחרונות)</span>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className={`border rounded-lg px-3 py-2 text-sm ${SEV_STYLES[a.severity] || SEV_STYLES.warning}`}>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs mt-0.5 opacity-80">{a.detail}</div>
                </div>
              ))}
            </div>
          )}

          {/* Stats row */}
          {data && (
            <div className="flex gap-4 text-xs text-gray-500 flex-wrap">
              <span>ריצות בחלון: {data.total_runs}</span>
              {data.failure_rate != null && <span>כשל: {data.failure_rate}%</span>}
              {data.pending_count > 0 && <span>ממתינות לאישור: {data.pending_count}</span>}
              {data.total_cost_usd != null && <span>עלות: ${data.total_cost_usd.toFixed(4)}</span>}
            </div>
          )}

          {/* Cooldown notice */}
          {data?.cooldown_remaining_hours != null && (
            <p className="text-xs text-gray-400">
              קולד-אאוט פעיל — ניתן לשלוח שוב בעוד {data.cooldown_remaining_hours.toFixed(1)} שעות
            </p>
          )}

          {dispatchMutation.data && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {dispatchMutation.data.dispatched > 0
                ? `נפתחו ${dispatchMutation.data.dispatched} קריאות: ${dispatchMutation.data.ticket_numbers.join(", ")}`
                : "אין התראות פעילות כרגע"}
            </div>
          )}

          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={() => refetch()} className="text-xs border px-3 py-1 rounded hover:bg-gray-50">
              רענן
            </button>
            {hasAlerts && (
              <button
                onClick={() => dispatchMutation.mutate()}
                disabled={dispatchMutation.isPending || data?.cooldown_remaining_hours != null}
                className="text-xs border border-red-200 text-red-700 px-3 py-1 rounded hover:bg-red-50 disabled:opacity-50"
              >
                {dispatchMutation.isPending ? "שולח..." : "צור קריאות לכל ההתראות"}
              </button>
            )}
            <button onClick={() => { setConfigOpen(!configOpen); setCfg(currentCfg); }} className="text-xs text-brand-600 hover:underline">
              הגדרות סף
            </button>
          </div>

          {/* Config panel */}
          {configOpen && (
            <div className="border rounded-lg p-3 bg-gray-50 space-y-3 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">סף כשל (%)</label>
                  <input type="number" min="1" max="100"
                    value={currentCfg.alert_failure_rate_pct ?? 50}
                    onChange={(e) => setCfg((f) => ({ ...f, alert_failure_rate_pct: e.target.value }))}
                    className="w-full border rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">סף צבר ריצות</label>
                  <input type="number" min="1"
                    value={currentCfg.alert_pending_backlog ?? 10}
                    onChange={(e) => setCfg((f) => ({ ...f, alert_pending_backlog: e.target.value }))}
                    className="w-full border rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">תקציב USD (0=כבוי)</label>
                  <input type="number" min="0" step="0.01"
                    value={currentCfg.alert_cost_budget_usd || 0}
                    onChange={(e) => setCfg((f) => ({ ...f, alert_cost_budget_usd: e.target.value }))}
                    className="w-full border rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">חלון שעות</label>
                  <input type="number" min="1" max="168"
                    value={currentCfg.alert_window_hours ?? 24}
                    onChange={(e) => setCfg((f) => ({ ...f, alert_window_hours: e.target.value }))}
                    className="w-full border rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">קולד-אאוט (שעות)</label>
                  <input type="number" min="1"
                    value={currentCfg.alert_cooldown_hours ?? 4}
                    onChange={(e) => setCfg((f) => ({ ...f, alert_cooldown_hours: e.target.value }))}
                    className="w-full border rounded px-2 py-1 text-xs" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => configMutation.mutate(currentCfg)} disabled={configMutation.isPending}
                  className="text-xs bg-brand-600 text-white px-3 py-1 rounded disabled:opacity-50">
                  שמור
                </button>
                <button onClick={() => setConfigOpen(false)} className="text-xs border px-3 py-1 rounded hover:bg-gray-50">
                  ביטול
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  // GET /api/reports/alerts, /alerts/config and /ai-operations require the Admin/Owner role: for a technician they
  // answered 403 (again every 2 minutes) and the page showed a permanent "AI metric status unknown" box.
  const role = useAuth()?.user?.role;
  const isAdmin = role === "admin" || role === "owner";
  return (
    <div className="space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-800">דוחות</h1>
      <Summary />
      {isAdmin && <AlertsPanel />}
      {isAdmin && <AiOperations />}
      <ByCompany />
      <ByTechnician />
      <ByTime />
    </div>
  );
}
